import { useCallback, useEffect } from "react";
import {
  InternalReportDownloadFormat,
  InternalReportType,
} from "../../../../api/tenantInternalReports";
import { useAppDispatch, useAppSelector } from "../../../../app/store";
import { ErrorDisplay } from "../../../../components/ui/error/ErrorDisplay";
import { CustomLoadingSpinner } from "../../../../components/ui/spinners/LoadingSpinner";
import { getCurrentMonthKey } from "../../../../lib/utils";
import InternalReportContainer from "../common/InternalReportContainer";
import ReportsPageLayout from "../common/ReportsPageLayout";
import { INTERNAL_REPORT_CONFIG } from "../reportsLanding/internalReportConfig";
import {
  selectBorrowalVsNonBorrowalError,
  selectBorrowalVsNonBorrowalSelectedPeriod,
  selectBorrowalVsNonBorrowalStatus,
} from "../../logic/borrowalVsNonBorrowalReportSlice";
import { fetchBorrowalVsNonBorrowalReportThunk } from "../../logic/borrowalVsNonBorrowalReportThunk";
import { downloadBorrowalVsNonBorrowalReportThunk } from "../../logic/borrowalVsNonBorrowalReportThunk";
import BorrowalVsNonBorrowalReport from "./BorrowalVsNonBorrowalReport";

const reportType = InternalReportType.BORROWAL_VS_NON_BORROWAL;

const BorrowalVsNonBorrowalReportPage: React.FC = () => {
  const dispatch = useAppDispatch();

  const status = useAppSelector(selectBorrowalVsNonBorrowalStatus);
  const error = useAppSelector(selectBorrowalVsNonBorrowalError);
  const selectedPeriod = useAppSelector(selectBorrowalVsNonBorrowalSelectedPeriod);

  const loadBorrowalVsNonBorrowalReport = useCallback(
    (period?: string) => {
      dispatch(fetchBorrowalVsNonBorrowalReportThunk({ period }));
    },
    [dispatch]
  );

  useEffect(() => {
    loadBorrowalVsNonBorrowalReport(getCurrentMonthKey());
  }, [loadBorrowalVsNonBorrowalReport]);

  const handleDownload = async (format: InternalReportDownloadFormat) => {
    await dispatch(
      downloadBorrowalVsNonBorrowalReportThunk({
        period: selectedPeriod ?? undefined,
        format,
      })
    );
  };

  const hasData = selectedPeriod !== null;

  return (
    <ReportsPageLayout>
      <InternalReportContainer
        title={INTERNAL_REPORT_CONFIG[reportType].title}
        onDownload={handleDownload}
        downloadEnabled={hasData}
      >
        {status === "failed" ? (
          <ErrorDisplay
            error={error ?? "Failed to load fraud overview"}
            onRetry={() =>
              loadBorrowalVsNonBorrowalReport(selectedPeriod ?? undefined)
            }
            isRetrying={false}
          />
        ) : !hasData ? (
          <CustomLoadingSpinner
            className="h-full w-full min-h-0 flex-1 m-0"
            title={`Loading ${INTERNAL_REPORT_CONFIG[reportType].title}...`}
          />
        ) : (
          <BorrowalVsNonBorrowalReport />
        )}
      </InternalReportContainer>
    </ReportsPageLayout>
  );
};

export default BorrowalVsNonBorrowalReportPage;
